import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ENQUIRY_TYPES, MEDIA_NATURES, MEDIA_EMAIL } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Field({ label, id, required = true, children }) {
    return (
        <div>
            <label htmlFor={id} className="field-label">
                {label}
                {required && <span className="ml-1 text-blue">*</span>}
            </label>
            {children}
        </div>
    );
}

function Consent({ id, checked, onChange, text }) {
    return (
        <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink-2">
            <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={`${id}-checkbox`}
                className="mt-1 h-4 w-4 shrink-0 rounded-none border border-line-2 accent-[#004B87]"
            />
            <span>{text}</span>
        </label>
    );
}

function SubmitButton({ id, label = "Submit", busy }) {
    return (
        <button type="submit" disabled={busy} data-testid={id} className="btn-primary disabled:cursor-wait disabled:opacity-60">
            {busy ? "Sending…" : label}
            <span aria-hidden="true" className="inline-block h-px w-6 bg-current" />
        </button>
    );
}

function errToast(e) {
    const detail = e?.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
}

export function ContactForm() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", enquiry_type: ENQUIRY_TYPES[0], message: "" });
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) return toast.error("Please provide GDPR consent so we can respond to your enquiry.");
        setBusy(true);
        try {
            await axios.post(`${API}/contact`, { ...form, consent });
            toast.success("Thank you — your enquiry has been received. We will respond shortly.");
            setForm({ name: "", email: "", phone: "", enquiry_type: ENQUIRY_TYPES[0], message: "" });
            setConsent(false);
        } catch (err) {
            errToast(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="contact-form" className="space-y-6" noValidate>
            <Field label="First and last name" id="cf-name">
                <input id="cf-name" required className="field-input" placeholder="Jane Andersen" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
            </Field>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Email" id="cf-email">
                    <input id="cf-email" type="email" required className="field-input" placeholder="name@company.com" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                </Field>
                <Field label="Phone" id="cf-phone" required={false}>
                    <input id="cf-phone" type="tel" className="field-input" placeholder="+45 ..." value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                </Field>
            </div>
            <div>
                <span className="field-label">Enquiry type *</span>
                <div role="radiogroup" aria-label="Enquiry type" className="flex flex-wrap gap-3">
                    {ENQUIRY_TYPES.map((t) => (
                        <label
                            key={t}
                            className={`cursor-pointer border px-5 py-2.5 font-display text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                                form.enquiry_type === t ? "border-blue bg-blue text-white" : "border-line-2 text-ink-2 hover:border-ink"
                            }`}
                        >
                            <input type="radio" name="enquiry_type" value={t} checked={form.enquiry_type === t} onChange={set("enquiry_type")} className="sr-only" data-testid={`contact-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                            {t}
                        </label>
                    ))}
                </div>
            </div>
            <Field label="Message" id="cf-message">
                <textarea id="cf-message" required rows={5} className="field-input resize-y" placeholder="How can we help?" value={form.message} onChange={set("message")} data-testid="contact-message-input" />
            </Field>
            <Consent id="cf-consent" checked={consent} onChange={setConsent} text="I agree that AddiGen may store and process my details in order to respond to my enquiry, in line with the Privacy Policy." />
            <SubmitButton id="contact-submit-button" label="Send enquiry" busy={busy} />
        </form>
    );
}

export function MediaForm() {
    const [form, setForm] = useState({ first_name: "", last_name: "", organisation: "", email: "", phone: "", nature: MEDIA_NATURES[0], deadline: "", message: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.post(`${API}/media-enquiry`, form);
            toast.success(`Thank you — your media enquiry has been received. Or write to ${MEDIA_EMAIL}.`);
            setForm({ first_name: "", last_name: "", organisation: "", email: "", phone: "", nature: MEDIA_NATURES[0], deadline: "", message: "" });
        } catch (err) {
            errToast(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="media-form" className="space-y-6" noValidate>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="First name" id="mf-first">
                    <input id="mf-first" required className="field-input" value={form.first_name} onChange={set("first_name")} data-testid="media-first-name-input" />
                </Field>
                <Field label="Last name" id="mf-last">
                    <input id="mf-last" required className="field-input" value={form.last_name} onChange={set("last_name")} data-testid="media-last-name-input" />
                </Field>
            </div>
            <Field label="Organisation / Publication" id="mf-org">
                <input id="mf-org" required className="field-input" value={form.organisation} onChange={set("organisation")} data-testid="media-org-input" />
            </Field>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Email" id="mf-email">
                    <input id="mf-email" type="email" required className="field-input" value={form.email} onChange={set("email")} data-testid="media-email-input" />
                </Field>
                <Field label="Phone" id="mf-phone" required={false}>
                    <input id="mf-phone" type="tel" className="field-input" value={form.phone} onChange={set("phone")} data-testid="media-phone-input" />
                </Field>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Nature of enquiry" id="mf-nature">
                    <select id="mf-nature" className="field-input" value={form.nature} onChange={set("nature")} data-testid="media-nature-select">
                        {MEDIA_NATURES.map((n) => (
                            <option key={n}>{n}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Deadline" id="mf-deadline" required={false}>
                    <input id="mf-deadline" type="date" className="field-input" value={form.deadline} onChange={set("deadline")} data-testid="media-deadline-input" />
                </Field>
            </div>
            <Field label="Message" id="mf-message">
                <textarea id="mf-message" required rows={5} className="field-input resize-y" value={form.message} onChange={set("message")} data-testid="media-message-input" />
            </Field>
            <SubmitButton id="media-submit-button" label="Send media enquiry" busy={busy} />
        </form>
    );
}

export function ApplicationForm({ jobId = "", jobTitle = "" }) {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", linkedin: "", country: "", phone: "", message: "" });
    const [cv, setCv] = useState(null);
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ok = /\.(pdf|docx?)$/i.test(f.name);
        if (!ok) return toast.error("CV must be a PDF or Word document.");
        if (f.size > 10 * 1024 * 1024) return toast.error("CV must be 10 MB or smaller.");
        setCv(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!cv) return toast.error("Please attach your CV (PDF or Word).");
        if (!consent) return toast.error("Please provide GDPR consent to apply.");
        setBusy(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            fd.append("job_id", jobId);
            fd.append("job_title", jobTitle);
            if (cv) fd.append("cv", cv);
            await axios.post(`${API}/applications`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Thank you — your application has been received.");
            setForm({ first_name: "", last_name: "", email: "", linkedin: "", country: "", phone: "", message: "" });
            setCv(null);
        } catch (err) {
            errToast(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="application-form" className="space-y-6" noValidate>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="First name" id="af-first">
                    <input id="af-first" required className="field-input" value={form.first_name} onChange={set("first_name")} data-testid="apply-first-name-input" />
                </Field>
                <Field label="Last name" id="af-last">
                    <input id="af-last" required className="field-input" value={form.last_name} onChange={set("last_name")} data-testid="apply-last-name-input" />
                </Field>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Email" id="af-email">
                    <input id="af-email" type="email" required className="field-input" value={form.email} onChange={set("email")} data-testid="apply-email-input" />
                </Field>
                <Field label="LinkedIn address" id="af-linkedin" required={false}>
                    <input id="af-linkedin" type="url" className="field-input" placeholder="https://linkedin.com/in/…" value={form.linkedin} onChange={set("linkedin")} data-testid="apply-linkedin-input" />
                </Field>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Home country" id="af-country">
                    <input id="af-country" required className="field-input" value={form.country} onChange={set("country")} data-testid="apply-country-input" />
                </Field>
                <Field label="Phone number" id="af-phone" required={false}>
                    <input id="af-phone" type="tel" className="field-input" value={form.phone} onChange={set("phone")} data-testid="apply-phone-input" />
                </Field>
            </div>
            <Field label="CV upload — PDF or Word only" id="af-cv">
                <input
                    id="af-cv"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={onFile}
                    data-testid="apply-cv-input"
                    className="w-full cursor-pointer rounded-none border border-line bg-white px-4 py-3 text-sm text-ink-2 file:mr-4 file:border-0 file:bg-blue file:px-4 file:py-2 file:font-mono file:text-[11px] file:uppercase file:tracking-[0.14em] file:text-white"
                />
                {cv && <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-blue">{cv.name}</p>}
            </Field>
            <Field label="Message to hiring team" id="af-message" required={false}>
                <textarea id="af-message" rows={4} className="field-input resize-y" value={form.message} onChange={set("message")} data-testid="apply-message-input" />
            </Field>
            <Consent id="af-consent" checked={consent} onChange={setConsent} text="I agree that AddiGen may store and process my application and personal data for the purpose of recruitment, in line with the Privacy Policy." />
            <SubmitButton id="apply-submit-button" label="Submit application" busy={busy} />
        </form>
    );
}
